import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { GuideCardGrid, GuideFooter } from "@/components/guide/GuideSections";
import { JsonLd } from "@/components/JsonLd";
import { KeyTakeaways } from "@/components/KeyTakeaways";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ReviewedBy } from "@/components/ReviewedBy";
import { SITE } from "@/content/site";
import { buildSeo, breadcrumbSchema } from "@/lib/seo";
import { faqSchema } from "@/lib/schema";

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Resources", path: "/resources" },
  { name: "SEO Expert in Bangladesh", path: "/seo-expert-bangladesh" },
];

const WORK = [
  {
    t: "Technical SEO audits",
    d: "Crawl, index and Core Web Vitals fixes — the work that unblocks everything else. Typical audit surfaces 40–120 issues ranked by revenue impact.",
  },
  {
    t: "Local SEO for Bangladesh",
    d: "Google Business Profile, NAP consistency, Bangla + English landing pages, and city-level pages for Dhaka, Chattogram, Sylhet, Khulna and Rangpur.",
  },
  {
    t: "Ecommerce SEO",
    d: "Category architecture, faceted navigation control, product schema, and internal linking that pushes authority to money pages.",
  },
  {
    t: "Content strategy",
    d: "Keyword-to-page mapping, topical hubs, and briefs your writers can actually ship — in English or Bengali.",
  },
  {
    t: "AEO and GEO",
    d: "Getting cited by ChatGPT, Perplexity, Claude, Gemini and Google AI Overviews. Most Bangladeshi agencies still don't offer this.",
  },
  {
    t: "Reporting you can defend",
    d: "Monthly reporting against one north-star KPI — pipeline or revenue, not vanity ranking screenshots.",
  },
];

const HIRE = [
  {
    t: "Ask for the last three engagements",
    d: "Not the logos — the actual numbers, the timeline, and what didn't work. Anyone with real experience will tell you about a project that flopped.",
  },
  {
    t: "Check whether they do technical work",
    d: 'Plenty of "SEO experts" in Bangladesh only write blog posts. Ask how they\'d fix a crawl-budget problem or a canonical conflict.',
  },
  {
    t: "Look for AI-search readiness",
    d: "A share of your future traffic is arriving through ChatGPT and AI Overviews. If your candidate has no answer for that, they're planning for 2019.",
  },
  {
    t: "Beware of ranking guarantees",
    d: "Nobody controls Google's index. A guarantee of #1 in 30 days means either paid placements or a link scheme you'll pay for later.",
  },
  {
    t: "Confirm who does the work",
    d: "Agencies sell you the senior person and staff the project with a junior. With a solo consultant, the person on the call is the person in the docs.",
  },
];

const PRICING = [
  {
    tier: "One-off technical audit",
    price: "৳90,000–৳2,50,000 (~$750–$2,500)",
    detail:
      "Full crawl, prioritised fix list, and a walkthrough call. Best if you have a dev team already.",
  },
  {
    tier: "Strategy retainer",
    price: "৳1,80,000+/mo (~$1,500+)",
    detail: "Roadmap, briefs, reviews and reporting. Your team executes; I direct.",
  },
  {
    tier: "Full execution retainer",
    price: "৳4,00,000–৳9,50,000/mo (~$3,500–$8,000)",
    detail: "Technical, content, AEO/GEO and local — hands-on across the whole program.",
  },
];

const FAQS = [
  {
    q: "Who is the best SEO expert in Bangladesh?",
    a: "Choose through relevant work, dated source records, clear scope and reporting access. Freelancer Tamal offers technical SEO, local SEO, ecommerce SEO, AEO and GEO from Rangpur; no independent best-in-country designation is established here.",
  },
  {
    q: "How much does an SEO expert cost in Bangladesh?",
    a: "In Bangladesh, a one-off technical SEO audit typically costs ৳90,000–৳2,50,000. Monthly retainers range from ৳1,80,000 for strategy-only work up to ৳9,50,000 for full execution. Cheap ৳15,000/month packages usually buy directory spam, not rankings.",
  },
  {
    q: "How long does SEO take to work in Bangladesh?",
    a: "Expect the first movement in 8–12 weeks and meaningful traffic gains in 4–6 months. Local searches in smaller cities like Rangpur or Sylhet usually face fewer competing pages than Dhaka, so they can move sooner — but no timeline is guaranteed.",
  },
  {
    q: "Should I hire an SEO freelancer or an SEO agency in Bangladesh?",
    a: "Compare the actual scope, responsibilities and quoted fees rather than assuming a fixed percentage saving. An agency makes more sense when you need several specialists working in parallel at high volume.",
  },
  {
    q: "Do you do SEO in Bengali as well as English?",
    a: "Yes. Many Bengali searches show low competition in SEO tools, and people in Bangladesh often type Bengali words in English letters (for example “seo ki”). I build parallel Bangla and English pages with correct hreflang so both index cleanly.",
  },
  {
    q: "Do you work with clients outside Bangladesh?",
    a: "Yes. Work can run remotely with written updates, a regular call and monthly reporting, so location does not limit the scope.",
  },
  {
    q: "Is an SEO expert in Rangpur different from one in Dhaka?",
    a: "The work is the same: technical fixes, content and local signals. What differs is competition — searches for smaller cities usually have fewer competing pages than Dhaka, so local results can be easier to win.",
  },
  {
    q: "How do I hire a freelance SEO expert?",
    a: "Check three things: real case studies you can verify, a clear written scope with what is not included, and honest timelines with no ranking guarantees. Start with a paid audit or short sprint before committing to a long retainer.",
  },
];

export const Route = createFileRoute("/seo-expert-bangladesh")({
  head: () =>
    buildSeo({
      title: "SEO Expert in Bangladesh: Freelance SEO Specialist & Pricing",
      description:
        "Looking for an SEO expert in Bangladesh? Compare SEO prices in taka, what the work includes, and how to choose a freelancer. Free audit from Rangpur.",
      path: "/seo-expert-bangladesh",
      ogEyebrow: "SEO Expert in Bangladesh",
      keywords:
        "freelance SEO expert, SEO expert Bangladesh, freelance SEO specialist Bangladesh, SEO consultant Bangladesh, SEO expert Rangpur, best SEO expert in Bangladesh",
      alternates: { bn: "/bn/blog/seo-expert-bangladesh-bangla" },
    }),
  component: SeoExpertBdPage,
});

function SeoExpertBdPage() {
  return (
    <>
      <Breadcrumbs items={CRUMBS} />

      <Section
        headingLevel={1}
        eyebrow="Bangladesh"
        title={
          <>
            Freelance <span className="text-gradient">SEO expert in Bangladesh</span> who ships
            technical work.
          </>
        }
        subtitle="SEO consulting from Rangpur for Bangladeshi businesses and international teams — technical audits, local search, ecommerce, and AI-search visibility."
      >
        <div className="max-w-3xl">
          <KeyTakeaways
            items={[
              "Freelancer Tamal is a freelance SEO specialist offering SEO, AEO and GEO consulting from Rangpur, Bangladesh.",
              "SEO in Bangladesh costs roughly ৳90,000–৳2,50,000 for a one-off audit, or ৳1,80,000–৳9,50,000/month for an ongoing retainer.",
              "Expect first movement in 8–12 weeks; local searches outside Dhaka often face less competition.",
              "Many Bengali searches show low competition — a parallel Bangla page set is often a cost-effective addition.",
            ]}
          />
          <ReviewedBy
            reviewedOn="2026-11-01"
            credentials="SEO, AEO and GEO consultant based in Rangpur, Bangladesh"
          />
        </div>
      </Section>

      <Section eyebrow="Scope" title="What an SEO expert actually does for you">
        <GuideCardGrid items={WORK} reveal check />
      </Section>

      <Section eyebrow="Pricing" title="What SEO costs in Bangladesh">
        <div className="overflow-x-auto -mt-4">
          <table className="w-full text-sm border-collapse min-w-[640px]">
            <thead>
              <tr className="text-left border-b border-border">
                <th className="py-3 pr-6 font-display font-normal">Engagement</th>
                <th className="py-3 pr-6 font-display font-normal">Typical range</th>
                <th className="py-3 font-display font-normal">Best for</th>
              </tr>
            </thead>
            <tbody>
              {PRICING.map((p) => (
                <tr key={p.tier} className="border-b border-border/60 align-top">
                  <td className="py-4 pr-6 font-medium">{p.tier}</td>
                  <td className="py-4 pr-6 text-muted-foreground whitespace-nowrap">{p.price}</td>
                  <td className="py-4 text-muted-foreground">{p.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-5 text-sm text-muted-foreground max-w-2xl">
          Ranges are indicative and depend on site size, competition and how much of the work your
          own team can absorb. Every quote starts with a scoping call, not a template.
        </p>
      </Section>

      <Section eyebrow="Hiring" title="How to choose an SEO expert in Bangladesh">
        <GuideCardGrid items={HIRE} reveal />
      </Section>

      <GuideFooter
        nextEyebrow="Where"
        nextTitle="Cities and services"
        nextIntro={
          <p className="max-w-3xl mb-8 text-muted-foreground">
            Comparing providers? Use the <Link to="/seo-services-bangladesh" className="text-primary underline underline-offset-4">Bangladesh SEO services scope and cost checklist</Link> to compare written quotes, deliverables and exclusions without treating one provider’s fees as a market average.
          </p>
        }
        links={[
          { label: "SEO by city", to: "/seo-locations" },
          { label: "SEO expert in Rangpur — scope & local services", to: "/seo-services/$location", params: { location: "rangpur" } },
          { label: "SEO by industry", to: "/seo-for" },
          { label: "All services", to: "/services" },
          { label: "Bangladesh SEO hub", to: "/bangladesh-seo" },
          { label: "Best digital marketing expert (BD)", to: "/best-digital-marketing-expert-bangladesh" },
        ]}
        aiContext="SEO in Bangladesh"
        aiQuestions={["seo-bangladesh-market", "seo-cost-bangladesh", "bangla-seo-worth-it", "local-seo-still-matter"]}
        faqs={FAQS}
        faqIdPrefix="faq-seo-expert-bd"
        ctaTitle={<>Need an <span className="text-gradient">SEO expert</span> who starts with the technical debt?</>}
      />

      <JsonLd data={faqSchema(FAQS)} />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          name: `${SITE.fullName} — SEO Expert in Bangladesh`,
          url: `${SITE.url}/seo-expert-bangladesh`,
          telephone: SITE.phone,
          email: SITE.email,
          areaServed: { "@type": "Country", name: SITE.country },
          address: {
            "@type": "PostalAddress",
            streetAddress: SITE.streetAddress,
            addressLocality: SITE.city,
            addressRegion: SITE.region,
            postalCode: SITE.postalCode,
            addressCountry: SITE.countryCode,
          },
          knowsAbout: [
            "Search engine optimization",
            "Technical SEO",
            "Local SEO",
            "Ecommerce SEO",
            "Answer Engine Optimization",
            "Generative Engine Optimization",
          ],
        }}
      />
      <JsonLd data={breadcrumbSchema(CRUMBS)} />
    </>
  );
}
